/**
 * TagSelector Component
 *
 * Displays optional multi-select tags for categorical choices.
 * Tags are SUGGESTIONS, not requirements - users can skip or type freely.
 * Includes "Other" option with text input when allowOther is true.
 */

import { useState } from 'react'
import { cn } from '@/lib/utils'
import { Check, X } from 'lucide-react'

export interface TagOption {
  id: string
  label: string
  description?: string
  category?: string
}

export interface TagSelection {
  type: 'tags'
  question: string
  options: TagOption[]
  multiSelect: boolean
  allowOther: boolean
  minSelect?: number
  maxSelect?: number
}

interface TagSelectorProps {
  selection: TagSelection
  onSubmit: (selectedIds: string[], otherText?: string) => void
  onSkip: () => void
  disabled?: boolean
  className?: string
}

export function TagSelector({
  selection,
  onSubmit,
  onSkip,
  disabled = false,
  className,
}: TagSelectorProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [otherText, setOtherText] = useState('')
  const [showOtherInput, setShowOtherInput] = useState(false)

  const handleTagClick = (id: string) => {
    if (disabled) return

    const newSelected = new Set(selectedIds)

    if (selection.multiSelect) {
      if (newSelected.has(id)) {
        newSelected.delete(id)
      } else {
        // Check max selection
        if (selection.maxSelect && newSelected.size >= selection.maxSelect) {
          return
        }
        newSelected.add(id)
      }
    } else {
      // Single select - replace
      newSelected.clear()
      newSelected.add(id)
    }

    setSelectedIds(newSelected)
  }

  const handleSubmit = () => {
    const ids = Array.from(selectedIds)
    if (ids.length === 0 && !otherText.trim()) {
      onSkip()
      return
    }
    onSubmit(ids, otherText.trim() || undefined)
  }

  const handleSingleSelect = (id: string) => {
    if (!selection.multiSelect) {
      // Single select submits immediately
      onSubmit([id], undefined)
    } else {
      handleTagClick(id)
    }
  }

  const canSubmit = selectedIds.size > 0 || otherText.trim()
  const minMet = !selection.minSelect || selectedIds.size >= selection.minSelect

  return (
    <div className={cn('space-y-3', className)}>
      {/* Question */}
      <p className="text-sm text-text-secondary">{selection.question}</p>

      {/* Tags Grid */}
      <div className="flex flex-wrap gap-2">
        {selection.options.map((option) => {
          const isSelected = selectedIds.has(option.id)
          return (
            <button
              key={option.id}
              onClick={() => handleSingleSelect(option.id)}
              disabled={disabled}
              title={option.description}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg',
                'border transition-all duration-200',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-solution focus-visible:ring-offset-2',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                isSelected
                  ? 'border-solution bg-solution/10 text-solution font-medium'
                  : 'border-border bg-surface text-text-secondary hover:bg-warm-50 hover:border-warm-400 hover:text-text'
              )}
            >
              {isSelected && <Check className="w-3.5 h-3.5" />}
              {option.label}
            </button>
          )
        })}

        {/* Other option button */}
        {selection.allowOther && !showOtherInput && (
          <button
            onClick={() => setShowOtherInput(true)}
            disabled={disabled}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg',
              'border border-dashed border-border-subtle',
              'text-text-muted hover:text-text hover:border-warm-400',
              'transition-all duration-200',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            + Other
          </button>
        )}
      </div>

      {/* Other text input */}
      {showOtherInput && (
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
            placeholder="Type your own..."
            disabled={disabled}
            className={cn(
              'flex-1 px-3 py-2 text-sm rounded-lg',
              'border border-border bg-surface',
              'text-text placeholder:text-text-muted',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-solution focus-visible:ring-offset-2',
              'disabled:opacity-50'
            )}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && otherText.trim()) {
                handleSubmit()
              }
            }}
          />
          <button
            onClick={() => {
              setShowOtherInput(false)
              setOtherText('')
            }}
            className="p-2 text-text-muted hover:text-text transition-colors"
            title="Cancel"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Action buttons (for multi-select) */}
      {selection.multiSelect && (
        <div className="flex items-center gap-3 pt-1">
          <button
            onClick={handleSubmit}
            disabled={disabled || !canSubmit || !minMet}
            className={cn(
              'px-4 py-2 text-sm font-medium rounded-lg',
              'bg-solution text-white',
              'hover:bg-solution/90 transition-colors',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            {canSubmit ? 'Continue' : 'Select options'}
          </button>
          <button
            onClick={onSkip}
            disabled={disabled}
            className={cn(
              'px-4 py-2 text-sm text-text-secondary',
              'hover:text-text transition-colors',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            Skip
          </button>
          {selection.minSelect && !minMet && (
            <span className="text-xs text-text-muted">
              Select at least {selection.minSelect}
            </span>
          )}
        </div>
      )}

      {/* Skip button for single select (appears below) */}
      {!selection.multiSelect && (
        <button
          onClick={onSkip}
          disabled={disabled}
          className={cn(
            'text-sm text-text-muted hover:text-text transition-colors',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
        >
          Skip this question
        </button>
      )}
    </div>
  )
}
